// On-screen pace notes (RALLY_GOAL.md §8): a strip of upcoming-note symbols in the style of
// modern rally games (bent arrow by severity, grade, modifiers, caution colour) and subtitles of
// what the co-driver says. DOM is rebuilt only when the set of visible notes changes.
import type { Call } from '../codriver/caller';
import type { Grade, PaceNote } from '../codriver/notes';

const SVG_NS = 'http://www.w3.org/2000/svg';

/** Turn angle drawn for each grade (degrees). */
const GRADE_ANGLE: Record<Grade, number> = { flat: 14, '6': 26, '5': 42, '4': 58, '3': 76, '2': 98, '1': 122, square: 90, acute: 140, hairpin: 180, open_hairpin: 160 };
const SHARP = new Set<Grade>(['square', 'acute']);

export const HAZARD_LABEL: Record<string, string> = {
  crest: 'CREST',
  over_crest: 'OVER CREST',
  jump: 'JUMP',
  big_jump: 'BIG JUMP',
  bump: 'BUMP',
  dip: 'DIP',
  narrows: 'NARROWS',
  bridge: 'BRIDGE',
  water_splash: 'WATER',
  onto_tarmac: 'TARMAC',
  onto_gravel: 'GRAVEL',
  keep_in: 'KEEP IN',
  keep_out: 'KEEP OUT',
  dont_cut: "DON'T CUT",
  cut: 'CUT',
  widens: 'WIDENS',
};

export function cautionLevel(n: PaceNote): number {
  const all = [...n.pre, ...n.post];
  if (all.includes('triple_caution')) return 3;
  if (all.includes('double_caution')) return 2;
  if (all.includes('caution')) return 1;
  return 0;
}

/** Arrow: a stem from the bottom that bends by the grade's angle; square/acute use a sharp corner. */
export function arrowPath(grade: Grade, dir: 'left' | 'right'): string {
  const sgn = dir === 'left' ? -1 : 1;
  const ang = (GRADE_ANGLE[grade] * Math.PI) / 180;
  let x = 50;
  let y = 92;
  const pts: string[] = [`M ${x} ${y}`];
  y -= 26;
  pts.push(`L ${x} ${y}`);
  let hx: number;
  let hy: number;
  let heading: number;
  if (SHARP.has(grade)) {
    heading = ang;
    const len = 30;
    x += sgn * Math.sin(heading) * len;
    y -= Math.cos(heading) * len;
    pts.push(`L ${x.toFixed(1)} ${y.toFixed(1)}`);
    hx = x;
    hy = y;
  } else {
    // Arc of fixed length; tighter grades turn more over the same length.
    const len = 52;
    const r = len / Math.max(ang, 0.2);
    const cx = x + sgn * r;
    const cy = y;
    heading = ang;
    x = cx - sgn * r * Math.cos(heading);
    y = cy - r * Math.sin(heading);
    pts.push(`A ${r.toFixed(1)} ${r.toFixed(1)} 0 0 ${dir === 'left' ? 0 : 1} ${x.toFixed(1)} ${y.toFixed(1)}`);
    hx = x;
    hy = y;
  }
  // Arrow head along the final heading.
  const fx = sgn * Math.sin(heading);
  const fy = -Math.cos(heading);
  const px = -fy;
  const py = fx;
  const h = 13;
  const w = 10;
  const tip = `${(hx + fx * h).toFixed(1)} ${(hy + fy * h).toFixed(1)}`;
  const l = `${(hx + px * w).toFixed(1)} ${(hy + py * w).toFixed(1)}`;
  const r2 = `${(hx - px * w).toFixed(1)} ${(hy - py * w).toFixed(1)}`;
  return `${pts.join(' ')}|M ${l} L ${tip} L ${r2}`;
}

function cornerSvg(n: PaceNote): SVGSVGElement {
  const svg = document.createElementNS(SVG_NS, 'svg');
  svg.setAttribute('viewBox', '0 0 100 100');
  svg.classList.add('pn-arrow');
  const [body, head] = arrowPath(n.grade!, n.dir!).split('|');
  for (const [d, cls] of [
    [body, 'pn-stem'],
    [head, 'pn-head'],
  ]) {
    const p = document.createElementNS(SVG_NS, 'path');
    p.setAttribute('d', d);
    p.setAttribute('class', cls);
    svg.appendChild(p);
  }
  return svg;
}

export function gradeLabel(n: PaceNote): string {
  switch (n.grade) {
    case 'flat':
      return 'FLAT';
    case 'square':
      return 'SQ';
    case 'acute':
      return 'AC';
    case 'hairpin':
      return 'HP';
    case 'open_hairpin':
      return 'OHP';
    default:
      return n.grade ?? '';
  }
}

/** Modifier labels shown under a tile (length, shape, hazards on a corner). */
export function modifiers(n: PaceNote): string[] {
  const mods: string[] = [];
  if (n.length) mods.push(n.length === 'long' ? 'LONG' : 'V.LONG');
  if (n.shape) mods.push(`${n.shape === 'opens' ? 'OPENS' : n.shape === 'tightens_late' ? 'TIGHTENS LATE' : 'TIGHTENS'}${n.shapeGrade ? ` ${gradeLabel({ ...n, grade: n.shapeGrade })}` : ''}`);
  if (n.kind === 'corner') for (const m of [...n.pre, ...n.post]) if (HAZARD_LABEL[m]) mods.push(HAZARD_LABEL[m]);
  return mods;
}

/** Label of a non-corner note (its hazard, else its text). */
export function hazardLabel(n: PaceNote): string {
  const key = n.pre.find((m) => HAZARD_LABEL[m]) ?? n.post.find((m) => HAZARD_LABEL[m]);
  return key ? HAZARD_LABEL[key] : n.text;
}

function tile(n: PaceNote): HTMLElement {
  const el = document.createElement('div');
  el.className = 'pn-tile';
  const c = cautionLevel(n);
  if (c) el.classList.add(`pn-caution-${c}`);
  if (n.kind === 'corner' && n.grade && n.dir) {
    el.appendChild(cornerSvg(n));
    const g = document.createElement('div');
    g.className = 'pn-grade';
    g.textContent = gradeLabel(n) + (n.refine === 'plus' ? '+' : n.refine === 'minus' ? '−' : '');
    el.appendChild(g);
  } else {
    const h = document.createElement('div');
    h.className = 'pn-hazard';
    h.textContent = hazardLabel(n);
    el.appendChild(h);
  }
  const mods = modifiers(n);
  if (mods.length) {
    const m = document.createElement('div');
    m.className = 'pn-mods';
    m.textContent = mods.join(' · ');
    el.appendChild(m);
  }
  return el;
}

export class PaceNoteHud {
  readonly root: HTMLElement;
  private readonly strip: HTMLElement;
  private readonly subtitle: HTMLElement;
  private upcoming: PaceNote[] = [];
  private shownKey = '';
  private subtitleUntil = 0;
  showSymbols = true;
  showSubtitles = true;
  /** The notes currently shown (up to three), for the co-driver's tablet; `version` bumps on change. */
  visible: PaceNote[] = [];
  version = 0;
  /** Sim time until which the current call is being read (the co-driver figure looks at his tablet). */
  private speakingUntil = 0;
  private lastNow = 0;
  private hidden = false;

  constructor(parent: HTMLElement) {
    this.root = document.createElement('div');
    this.root.className = 'pn-root';
    this.strip = document.createElement('div');
    this.strip.className = 'pn-strip';
    this.subtitle = document.createElement('div');
    this.subtitle.className = 'pn-subtitle';
    this.root.append(this.strip, this.subtitle);
    parent.appendChild(this.root);
  }

  onCall(call: Call, now: number): void {
    this.speakingUntil = now + call.duration;
    for (const n of call.notes) if (n.kind !== 'stage' && !this.upcoming.includes(n)) this.upcoming.push(n);
    this.upcoming.sort((a, b) => a.s - b.s);
    if (this.showSubtitles) {
      this.subtitle.textContent = call.tokens.join(' ').toUpperCase();
      this.subtitle.classList.add('on');
      this.subtitleUntil = now + call.duration + 1.2;
    }
  }

  /** In the cockpit view the notes move onto the co-driver's tablet: hide the strip (not the subtitles). */
  set stripHidden(on: boolean) {
    if (on === this.hidden) return;
    this.hidden = on;
    this.strip.classList.toggle('pn-hidden', on);
  }

  get speaking(): boolean {
    return this.lastNow < this.speakingUntil;
  }

  clear(): void {
    this.upcoming = [];
    this.subtitle.classList.remove('on');
    this.render();
  }

  /** Per frame: drop notes the car has reached, hide stale subtitles. */
  update(s: number, now: number): void {
    this.lastNow = now;
    while (this.upcoming.length && s > this.upcoming[0].s + 6) this.upcoming.shift();
    if (now > this.subtitleUntil || !this.showSubtitles) this.subtitle.classList.remove('on');
    this.render();
  }

  private render(): void {
    const vis = this.showSymbols ? this.upcoming.slice(0, 3) : [];
    const key = vis.map((n) => n.id).join(',');
    if (key === this.shownKey) return;
    this.shownKey = key;
    this.visible = vis;
    this.version++;
    this.strip.replaceChildren();
    vis.forEach((n, i) => {
      const t = tile(n);
      if (i === 0) t.classList.add('pn-next');
      this.strip.appendChild(t);
      if (i < vis.length - 1 && n.link) {
        const l = document.createElement('div');
        l.className = 'pn-link';
        l.textContent = n.link === 'into' ? 'INTO' : n.link === 'and' ? 'AND' : n.link;
        this.strip.appendChild(l);
      }
    });
  }
}
